import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import type { CognitoUser } from '../../types/cognitoUser';

interface UserListProps {
  users: CognitoUser[];
  isLoading: boolean;
  globalFilter: string;
  onGlobalFilterChange: (value: string) => void;
  onRowClick: (user: CognitoUser) => void;
  onCreateUser: () => void;
}

export const UserList: React.FC<UserListProps> = ({
  users,
  isLoading,
  globalFilter,
  onGlobalFilterChange,
  onRowClick,
  onCreateUser,
}) => {
  const enabledBodyTemplate = (rowData: CognitoUser) => (
    <div style={{ textAlign: 'center' }}>
      <i className={rowData.enabled ? 'pi pi-check' : 'pi pi-times'}
         style={{ color: rowData.enabled ? '#22c55e' : '#ef4444' }} />
    </div>
  );

  const statusBodyTemplate = (rowData: CognitoUser) => {
    const status = rowData.userStatus || '';
    const color = status === 'CONFIRMED' ? '#22c55e' : '#f59e0b';
    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 600,
        backgroundColor: `${color}20`,
        color,
      }}>
        {status}
      </span>
    );
  };

  const createdAtBodyTemplate = (rowData: CognitoUser) => {
    if (!rowData.createdAt) return '';
    return new Date(rowData.createdAt).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Button
        label="Create User"
        icon="pi pi-plus"
        className="p-button-success"
        onClick={onCreateUser}
      />
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search" />
        <InputText
          type="search"
          value={globalFilter}
          onInput={(e) => onGlobalFilterChange((e.target as HTMLInputElement).value)}
          placeholder="Search users..."
        />
      </IconField>
    </div>
  );

  return (
    <DataTable
      value={users}
      header={header}
      loading={isLoading}
      paginator
      rows={10}
      rowsPerPageOptions={[10, 25, 50]}
      globalFilter={globalFilter}
      emptyMessage="No users found."
      selectionMode="single"
      onRowSelect={(e) => onRowClick(e.data as CognitoUser)}
      dataKey="username"
      sortField="username"
      sortOrder={1}
      stripedRows
      showGridlines
      size="small"
    >
      <Column field="username" header="Username" sortable style={{ minWidth: '12rem' }} />
      <Column field="email" header="Email" sortable style={{ minWidth: '14rem' }} />
      <Column field="name" header="Name" sortable style={{ minWidth: '10rem' }} />
      <Column field="enabled" header="Enabled" body={enabledBodyTemplate} sortable style={{ width: '6rem', textAlign: 'center' }} />
      <Column field="userStatus" header="Status" body={statusBodyTemplate} sortable style={{ minWidth: '10rem' }} />
      <Column field="createdAt" header="Created" body={createdAtBodyTemplate} sortable style={{ minWidth: '8rem' }} />
    </DataTable>
  );
};
